import React from 'react'
import { addYears } from 'date-fns/addYears'
import { getDayOfYear } from 'date-fns/getDayOfYear'
import { setDayOfYear } from 'date-fns/setDayOfYear'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import Stack from '@mui/material/Stack'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { Select, useGlobalCopyStore } from '../../common'
import { Frequency } from '../../core'
import { formatOrdinal } from '../../core/utils/NumberFormatting'

interface PaymentScheduleProps {
  frequency: Frequency
  firstDay: number
  secondDay?: number
  onFrequencyChange: (frequency: Frequency) => void
  onFirstDayChange: (day: number) => void
  onSecondDayChange: (day: number) => void
}

export const PaymentSchedule = ({
  frequency,
  firstDay,
  secondDay,
  onFrequencyChange,
  onFirstDayChange,
  onSecondDayChange,
}: PaymentScheduleProps) => {
  const { recurring: copy } = useGlobalCopyStore()

  const frequencies = React.useMemo(
    () => [
      { id: Frequency.EveryWeek, label: copy.frequency_every_week },
      { id: Frequency.EveryOtherWeek, label: copy.frequency_every_other_week },
      { id: Frequency.TwiceAMonth, label: copy.frequency_twice_a_month },
      { id: Frequency.EveryMonth, label: copy.frequency_every_month },
      { id: Frequency.EveryOtherMonth, label: copy.frequency_every_other_month },
      { id: Frequency.EveryQuarter, label: copy.frequency_every_quarter },
      { id: Frequency.EveryOtherQuarter, label: copy.frequency_every_other_quarter },
      { id: Frequency.EveryYear, label: copy.frequency_every_year },
    ],
    [copy],
  )

  const daysOfWeek = [
    { id: 0, label: copy.weekday_sunday },
    { id: 1, label: copy.weekday_monday },
    { id: 2, label: copy.weekday_tuesday },
    { id: 3, label: copy.weekday_wednesday },
    { id: 4, label: copy.weekday_thursday },
    { id: 5, label: copy.weekday_friday },
    { id: 6, label: copy.weekday_saturday },
  ]

  const daysOfMonth = [...Array(31).keys()].map((day) => ({
    id: day + 1,
    label: formatOrdinal(day + 1),
  }))
  daysOfMonth[30].label += ` (${copy.last_day_of_month})`

  const beginDaysOfMonth = daysOfMonth.slice(0, 15)
  const endDaysOfMonth = daysOfMonth.slice(14)

  const weeklyType = frequency === Frequency.EveryWeek || frequency === Frequency.EveryOtherWeek
  const monthlyType =
    frequency === Frequency.TwiceAMonth ||
    frequency === Frequency.EveryMonth ||
    frequency === Frequency.EveryOtherMonth ||
    frequency === Frequency.EveryQuarter ||
    frequency === Frequency.EveryOtherQuarter

  const tempDayOfYear = setDayOfYear(startOfToday(), firstDay)
  const dayOfYear = tempDayOfYear < startOfToday() ? addYears(tempDayOfYear, 1) : tempDayOfYear

  const handleYearlyChange = (date: Date | null) => {
    if (date) {
      onFirstDayChange(getDayOfYear(date))
    }
  }

  return (
    <Stack gap={24} sx={{ m: 24 }}>
      <Select
        label={copy.frequency}
        onChange={onFrequencyChange}
        options={frequencies}
        value={frequency}
      />
      {weeklyType && (
        <Select
          label={copy.day_of_the_week}
          onChange={onFirstDayChange}
          options={daysOfWeek}
          value={firstDay}
        />
      )}
      {monthlyType && (
        <Select
          label={
            frequency === Frequency.TwiceAMonth
              ? copy.first_day_of_the_month
              : copy.day_of_the_month
          }
          onChange={onFirstDayChange}
          options={frequency === Frequency.TwiceAMonth ? beginDaysOfMonth : daysOfMonth}
          value={firstDay}
        />
      )}
      {frequency === Frequency.TwiceAMonth && (
        <Select
          label={copy.second_day_of_the_month}
          onChange={onSecondDayChange}
          options={endDaysOfMonth}
          value={secondDay ?? ''}
        />
      )}
      {frequency === Frequency.EveryYear && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            disablePast={true}
            label={copy.next_payment_date}
            maxDate={subDays(addYears(startOfToday(), 1), 1)}
            minDate={startOfToday()}
            onChange={handleYearlyChange}
            value={dayOfYear}
          />
        </LocalizationProvider>
      )}
    </Stack>
  )
}
